import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';

import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

export interface DotChartData {
  label: string;
  value: number;
  color: string;
}

@Component({
  selector: 'app-dot-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dot-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
        display: block;
        width: 100%;
        height: 100%;
    }
    `
})
export class DotChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Input() data: DotChartData[] = [];
  
  @Input() marginTop: number = 20;
  @Input() marginRight: number = 20;
  @Input() marginBottom: number = 20;
  @Input() marginLeft: number = 40;
  
  @Input() scaleType: 'linear' | 'log' = 'linear';
  
  private chartId = Math.random().toString(36).substring(2, 9);
  private themeService = inject(ThemeService);
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  currentTheme: 'light' | 'dark' = 'dark';

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.drawChart(); // Redraw chart with new theme colors
    });
    this.drawChart();
  }

  ngOnDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] || changes['scaleType']) this.drawChart();
  }

  private drawChart() {
    if (!this.data?.length) return;
    const container: HTMLElement = this.chartContainer.nativeElement;
    const parent: HTMLElement | null = container.parentElement;
    if (!parent) return;

    // Clear old SVG + tooltip
    d3.select(container).selectAll('*').remove();
    d3.select(parent).selectAll('.chart-tooltip').remove();

    const colors = this.getColors();

    const rect = container.getBoundingClientRect();
    const width = rect.width - this.marginLeft - this.marginRight;
    // const height = rect.height - this.marginTop - this.marginBottom; // If height is fixed by input or css
    // If height should be dynamic or fixed min height:
    const height = (container.clientHeight || 400) - this.marginTop - this.marginBottom;

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${rect.width} ${container.clientHeight || 400}`)
      .append('g')
      .attr('transform', `translate(${this.marginLeft}, ${this.marginTop})`);

    // X Scale (categorical/point)
    const x = d3.scalePoint()
      .domain(this.data.map(d => d.label))
      .range([0, width])
      .padding(0.5);

    // Y Scale (Linear or Logarithmic)
    let y: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number>;
    let yTickValues: number[] = [];

    const maxVal = d3.max(this.data, d => d.value) || 10;
    const minVal = d3.min(this.data, d => d.value > 0 ? d.value : null) || 1;

    if (this.scaleType === 'log') {
      // Handle 0 or negative values by clamping to 1 for log scale
      y = d3.scaleLog()
        .domain([Math.max(1, minVal * 0.8), maxVal * 1.5]) // Add headroom and floor
        .range([height, 0])
        .nice();
      
      // Generate powers of 10 for ticks (1, 10, 100, 1000...)
      const domain = y.domain();
      const minPower = Math.floor(Math.log10(domain[0]));
      const maxPower = Math.ceil(Math.log10(domain[1]));
      yTickValues = [];
      for (let i = minPower; i <= maxPower; i++) {
        const val = Math.pow(10, i);
        if (val >= domain[0] && val <= domain[1]) {
           yTickValues.push(val);
        }
      }
      
      // If no ticks generated (e.g. small range), fallback to standard ticks
      if (yTickValues.length === 0) {
          yTickValues = y.ticks(5);
      }

    } else {
      // Linear Scale
      y = d3.scaleLinear()
        .domain([0, maxVal * 1.2]) // Add headroom
        .range([height, 0])
        .nice();
        
      yTickValues = y.ticks(5);
    }

    // Horizontal Grid Lines & Y Axis
    const yAxisContainer = svg.append('g')
      .attr('class', 'y-axis');

    // Add Grid Lines
    yAxisContainer.append('g')
      .attr('class', 'grid y-grid')
      .call(d3.axisLeft(y)
        .tickValues(yTickValues) // Use explicit tick values
        .tickSize(-width)
        .tickFormat(() => '') 
      )
      .selectAll('line')
      .attr('stroke', colors.axisLine)
      .attr('stroke-dasharray', '4 4')
      .attr('stroke-opacity', 0.5);

    // Add Main Y Axis Line (Pronounced)
    yAxisContainer.append('path')
      .attr('d', `M0,0 V${height}`)
      .attr('stroke', colors.axisLine)
      .attr('stroke-width', 2)
      .attr('fill', 'none');

    yAxisContainer.select('.y-grid .domain').remove();


     // Vertical Grid Lines (for each Point) & X Axis
     const xAxisContainer = svg.append('g')
     .attr('class', 'x-axis')
     .attr('transform', `translate(0, ${height})`);
     
     xAxisContainer.append('g')
     .attr('class', 'grid x-grid')
     .call(d3.axisBottom(x)
       .tickSize(-height)
       .tickFormat(() => '')
     )
     .selectAll('line')
     .attr('stroke', colors.axisLine)
     .attr('stroke-dasharray', '4 4')
     .attr('stroke-opacity', 0.5);

    // Add Main X Axis Line (Pronounced)
    xAxisContainer.append('path')
      .attr('d', `M0,0 H${width}`)
      .attr('stroke', colors.axisLine)
      .attr('stroke-width', 2)
      .attr('fill', 'none');
   
    xAxisContainer.select('.x-grid .domain').remove();


    // Y Axis Labels
    // For log scale, we might want specific tick formatting
    svg.append('g')
      .attr('class', 'y-axis-labels')
      .call(d3.axisLeft(y)
        .tickValues(yTickValues) // Use same explicit tick values
        .tickFormat(d3.format(',.0f')) // Use readable number format
        .tickSize(0)
      )
      .selectAll('text')
      .attr('x', -10) // added padding
      .style('fill', colors.labelColor)
      .style('font-size', '12px')
      .style('font-weight', '500'); // slightly bolder
      
    svg.select('.y-axis-labels .domain').remove();


    // Dots
    svg.selectAll('.dot')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('cx', d => x(d.label)!)
      .attr('cy', d => y(d.value) || 0)
      .attr('r', 8) // Radius of dots
      .attr('fill', d => d.color)
      .style('cursor', 'pointer')
      .on('mouseover', (event, d) => {
        // Tooltip logic
         tooltip
          .style('opacity', '1')
          .style('transform', 'translateY(0)')
          .html(
            `
        <div style="display:flex;flex-direction:column;">
          <div style="font-size:12px;font-weight:500;color:${colors.subtitleColor};margin-bottom:4px;">
            ${d.label}
          </div>
          <div style="font-size:14px;font-weight:600;color:${colors.text};">
            Session Time: ${d.value} min
          </div>
        </div>
      `
          )
          .style('left', `${event.clientX - parent.getBoundingClientRect().left + 16}px`)
          .style('top', `${event.clientY - parent.getBoundingClientRect().top - 10}px`);
      })
      .on('mousemove', (event) => {
         tooltip
          .style('left', `${event.clientX - parent.getBoundingClientRect().left + 16}px`)
          .style('top', `${event.clientY - parent.getBoundingClientRect().top - 10}px`);
      })
      .on('mouseleave', () => {
        tooltip.style('opacity', '0').style('transform', 'translateY(-4px)');
      });


     // Tooltip div
     const tooltip = d3
     .select(parent)
     .append('div')
     .attr('class', 'chart-tooltip')
     .style('opacity', '0')
     .style('position', 'absolute')
     .style('pointer-events', 'none')
     .style('background', colors.background)
     .style('border', colors.border)
     .style('border-radius', '8px')
     .style('padding', '8px 12px')
     .style('box-shadow', colors.shadow)
     .style('color', colors.text)
     .style('font-family', 'Inter, sans-serif')
     .style('white-space', 'nowrap')
     .style('transition', 'opacity 0.15s ease, transform 0.15s ease')
     .style('transform', 'translateY(-4px)')
     .style('z-index', '1000');


    // Axis Label (Session Time)
    // svg.append('text')
    //   .attr('transform', 'rotate(-90)')
    //   .attr('y', 0 - this.marginLeft)
    //   .attr('x', 0 - (height / 2))
    //   .attr('dy', '1em')
    //   .style('text-anchor', 'middle')
    //   .style('fill', colors.labelColor)
    //   .style('font-size', '12px')
    //   .text('Session Time');
  }

  private getColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      background: isDark ? '#1e1e21' : '#FFFFFF', // tailwind gray-800 : white
      text: isDark ? '#f4f4f5' : '#111827',
      shadow: isDark ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      border: isDark ? '1px solid #374151' : '1px solid #E5E7EB',
      subtitleColor: isDark ? '#a1a1aa' : '#6B7280',
      axisLine: isDark ? '#47474e' : '#E5E7EB',
      labelColor: isDark ? '#a1a1aa' : '#6B7280',
    };
  }
}
